import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../lib/firebase';
import { useAuth } from '../auth/AuthProvider';
import { Button } from '../components/ui/Button';
import { Alert } from '../components/ui/Alert';
import { Field, TextInput } from '../components/ui/Field';
import { usePageTitle } from '../hooks/usePageTitle';

interface LocationState {
  from?: { pathname: string };
}

export default function LoginPage() {
  usePageTitle('Sign in');
  const { user, signIn, loading, error } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [resetSentTo, setResetSentTo] = useState<string | null>(null);
  const [resetError, setResetError] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as LocationState | null)?.from?.pathname ?? '/';

  useEffect(() => {
    if (user && !loading) navigate(from, { replace: true });
  }, [user, loading, from, navigate]);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await signIn(email.trim(), password);
    } catch {
      /* error surfaced via context */
    } finally {
      setSubmitting(false);
    }
  }

  // Same Firebase reset email as the iPhone app's "Forgot Password?". Firebase
  // sends it to the inbox; nobody else ever sees the link. Its wording can't
  // reveal whether the address has a login: with email enumeration protection
  // on, Firebase reports success either way.
  async function sendResetLink() {
    const address = email.trim();
    setResetError(null);
    setResetSentTo(null);
    if (!address) {
      setResetError('Type your email address above first.');
      return;
    }
    setResetting(true);
    try {
      await sendPasswordResetEmail(auth, address, { url: `${window.location.origin}/login` });
      setResetSentTo(address);
    } catch (e) {
      const code = (e as { code?: string } | null)?.code;
      if (code === 'auth/user-not-found') {
        // Only possible with enumeration protection off (or in the emulator):
        // answer exactly as for a real login, so the page never says who has one.
        setResetSentTo(address);
        return;
      }
      setResetError(
        code === 'auth/invalid-email'
          ? 'That email address doesn’t look right.'
          : 'We couldn’t send the reset email. Check your connection and try again.'
      );
    } finally {
      setResetting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface px-4 py-10">
      <div className="w-full max-w-[420px]">
        {/* The mark belongs on the icon, the launch screen and sign-in — nowhere else. */}
        <div className="mb-7 text-center">
          <img src="/checkover-icon.png" alt="" className="mx-auto mb-4 h-[72px] w-[72px] rounded-[16px]" />
          <h1 className="text-[30px] font-bold tracking-[-0.035em] text-ink">Checkover</h1>
          <p className="mt-1.5 text-[15px] text-ink2">Sign in to your operations console.</p>
        </div>

        <div className="rounded-[14px] border border-hair bg-elev p-6 sm:p-7">
          <form onSubmit={onSubmit} className="flex flex-col gap-4">
            <Field label="Email">
              <TextInput
                type="email"
                required
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.co.uk"
              />
            </Field>

            <Field label="Password" htmlFor="password">
              <div className="relative">
                <TextInput
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((shown) => !shown)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  className="absolute right-1.5 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-[8px] text-ink3 hover:bg-hover hover:text-ink"
                >
                  <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden>
                    <path d="M1.8 11C4.5 6.5 7.6 4.6 11 4.6s6.5 1.9 9.2 6.4c-2.7 4.5-5.8 6.4-9.2 6.4S4.5 15.5 1.8 11Z" />
                    <circle cx="11" cy="11" r="3" />
                    {!showPassword && <path d="M3.5 3.5l15 15" />}
                  </svg>
                </button>
              </div>
            </Field>

            {error && <Alert tone="error">{error}</Alert>}
            {resetError && <Alert tone="error">{resetError}</Alert>}
            {resetSentTo && (
              <Alert tone="success">
                If {resetSentTo} has a Checkover login, we’ve emailed it a link to set a new password.
                Check your spam folder if it hasn’t arrived in a few minutes.
              </Alert>
            )}

            <Button type="submit" size="lg" loading={submitting} className="mt-1 w-full">
              {submitting ? 'Signing in…' : 'Sign in'}
            </Button>

            <Button type="button" variant="ghost" loading={resetting} onClick={() => void sendResetLink()} className="w-full">
              Forgot password?
            </Button>
          </form>
        </div>

        <p className="mt-5 text-center text-[13px] text-ink3">
          An administrator adds you to a company. Ask them if you cannot sign in.
        </p>
      </div>
    </div>
  );
}
